import React from 'react';
import { Phone, Mail, Globe } from 'lucide-react';
import type { PublicCompanyProfile } from '@/hooks/useCompanyProfile';

interface BrandedHeaderProps {
  title: string;
  subtitle?: string;
  showContactInfo?: boolean;
  companyProfile: PublicCompanyProfile;
}

export const BrandedHeader: React.FC<BrandedHeaderProps> = ({
  title,
  subtitle,
  showContactInfo = true,
  companyProfile
}) => {
  const companyName = companyProfile.company_name;
  const mainPhone = companyProfile.main_phone;
  const mainEmail = companyProfile.main_email;
  const website = companyProfile.website;

  return (
    <div className="relative bg-gradient-to-r from-white via-cyan-50/30 to-blue-50/30 shadow-lg border-b border-cyan-200/30">
      {/* Decorative gradient accent */}
      <div className="absolute inset-0 bg-gradient-to-r from-transparent via-cyan-400/5 to-transparent pointer-events-none" />

      <div className="relative max-w-7xl mx-auto px-8 py-10">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-8">
          {/* Left Side - Page Title */}
          <div className="flex-1">
            <h1 className="text-4xl font-bold bg-gradient-to-r from-gray-900 via-cyan-900 to-blue-900 bg-clip-text text-transparent">
              {title}
            </h1>
            {subtitle && (
              <p className="text-gray-700 mt-3 text-base">{subtitle}</p>
            )}
          </div>

          {/* Right Side - Company Branding */}
          <div className="text-left md:text-right">
            {/* Company Logo with glassmorphic container */}
            {companyProfile.logo_url && (
              <div className="relative inline-block glass-card px-4 py-2 mb-3 md:ml-auto">
                <img
                  src={companyProfile.logo_url}
                  alt={`${companyName} Logo`}
                  className="h-14 transition-transform hover:scale-105 duration-300"
                />
              </div>
            )}

            <div className="text-2xl font-bold bg-gradient-to-r from-cyan-600 to-blue-600 bg-clip-text text-transparent mb-2">
              {companyName}
            </div>

            {showContactInfo && (
              <div className="flex flex-col gap-2 text-sm text-gray-700">
                {mainPhone && (
                  <a
                    href={`tel:${mainPhone.replace(/\D/g, '')}`}
                    className="group flex items-center gap-2 hover:text-cyan-600 transition-all duration-300"
                  >
                    <Phone className="w-4 h-4 group-hover:rotate-12 transition-transform" />
                    <span className="font-medium">{mainPhone}</span>
                  </a>
                )}
                {mainEmail && (
                  <a
                    href={`mailto:${mainEmail}`}
                    className="group flex items-center gap-2 hover:text-cyan-600 transition-all duration-300"
                  >
                    <Mail className="w-4 h-4 group-hover:-rotate-12 transition-transform" />
                    <span className="font-medium">{mainEmail}</span>
                  </a>
                )}
                {website && (
                  <a
                    href={website.startsWith('http') ? website : `https://${website}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="group flex items-center gap-2 hover:text-cyan-600 transition-all duration-300"
                  >
                    <Globe className="w-4 h-4 group-hover:rotate-12 transition-transform" />
                    <span className="font-medium">{website.replace(/^https?:\/\//, '')}</span>
                  </a>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
